import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, apiDelete } from '../api/client.js';
import { ConnectionsSection } from '../components/ConnectionsSection.jsx';
import Sparkline from '../components/Sparkline.jsx';
import { ProjectStats } from '../components/ProjectStats.jsx';
import { ProjectSamples } from '../components/ProjectSamples.jsx';
import { BrandBrief } from '../components/BrandBrief.jsx';
import { NotificationEmails } from '../components/NotificationEmails.jsx';
import { MarketingCycle } from '../components/MarketingCycle.jsx';
import { PostFilters, buildPostsQuery } from '../components/PostFilters.jsx';
import { PostListRow } from '../components/PostListRow.jsx';
import { pillarLabel, fmtAgo } from '../lib/labels.js';

const TARGET_METRIC_OPTIONS = [
  { value: '', label: 'AI decides' },
  { value: 'saves', label: 'Saves' },
  { value: 'shares', label: 'Shares' },
  { value: 'follows_gained', label: 'Follows' },
  { value: 'profile_visits', label: 'Profile visits' },
  { value: 'comments', label: 'Comments' },
];

const CADENCE_LABEL = { daily: 'Daily', biweekly: 'Twice a week', weekly: 'Weekly', monthly: 'Monthly' };
const METRIC_LABEL = {
  saves: 'Saves',
  shares: 'Shares',
  follows_gained: 'Follows gained',
  profile_visits: 'Profile visits',
  comments: 'Comments',
};

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Convert a Markaii pillar cron expression to a short human-readable string.
 * All Markaii pillars use the shape:  <min> <hour> * * <day|*>
 * Examples:
 *   "47 12 * * 1"  →  "Mon · 12:47 UTC"
 *   "17 12 * * *"  →  "Daily · 12:17 UTC"
 */
function humanCron(expr) {
  if (!expr) return expr;
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [min, hour, , , dow] = parts;
  const hh = hour.padStart(2, '0');
  const mm = min.padStart(2, '0');
  const time = `${hh}:${mm} UTC`;
  if (dow === '*') return `Daily · ${time}`;
  const dayIndex = parseInt(dow, 10);
  const dayName = Number.isFinite(dayIndex) && dayIndex >= 0 && dayIndex <= 6
    ? WEEKDAY[dayIndex]
    : dow;
  return `${dayName} · ${time}`;
}

const PROJECT_TABS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'posts',       label: 'Posts' },
  { id: 'stats',       label: 'Stats' },
  { id: 'plan',        label: 'Plan' },
  { id: 'pillars',     label: 'Pillars' },
  { id: 'connections', label: 'Connections' },
  { id: 'samples',     label: 'Samples' },
];
const PROJECT_TAB_IDS = new Set(PROJECT_TABS.map((t) => t.id));

export default function ProjectDetail() {
  const { key } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = PROJECT_TAB_IDS.has(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
  const goTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const projQ = useQuery({
    queryKey: ['project', key],
    queryFn: ({ signal }) => apiGet(`/projects/${key}`, { signal }),
  });
  const planQ = useQuery({
    queryKey: ['plan', { project: key }],
    queryFn: ({ signal }) => apiGet(`/plans/active?project=${encodeURIComponent(key)}`, { signal }),
    // 404 (no plan yet) is expected for fresh brands — don't retry
    retry: (count, err) => err?.status !== 404 && count < 1,
  });
  const draftPlanQ = useQuery({
    queryKey: ['plan-draft', { project: key }],
    queryFn: ({ signal }) => apiGet(`/plans/draft?project=${encodeURIComponent(key)}`, { signal }),
    // 404 = no draft — normal state
    retry: (count, err) => err?.status !== 404 && count < 1,
  });
  const loopQ = useQuery({
    queryKey: ['loop-status', key],
    queryFn: ({ signal }) => apiGet(`/projects/${encodeURIComponent(key)}/loop-status`, { signal }),
    // Refresh when other actions (fire, approve, regenerate plan) finish
    staleTime: 15_000,
  });

  if (projQ.isLoading) return <div className="loading">Loading project…</div>;
  if (projQ.isError) return <div className="error">Couldn’t load project: {projQ.error.message}</div>;
  const { project, pillars } = projQ.data;

  const draftBadge = draftPlanQ.data && planQ.data;

  return (
    <div className="project-detail">
      <Link to="/projects" className="back-link">← Projects</Link>

      <header className="project-header">
        <h1>{project.display_name}</h1>
        {project.ig_handle && <span className="ig-handle">{project.ig_handle}</span>}
        {/* Positioning lives in the Brand Brief readout below — not duplicated here. */}
      </header>

      <nav className="project-tabs" aria-label="Project sections">
        {PROJECT_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-current={tab === t.id ? 'page' : undefined}
            className={`project-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => goTab(t.id)}
          >
            {t.label}
            {t.id === 'plan' && draftBadge && <span className="tab-dot" title="Draft plan awaiting review" />}
          </button>
        ))}
      </nav>

      {tab === 'overview' && (
        <>
          <BrandBrief projectKey={key} project={project} />
          <NotificationEmails projectKey={key} project={project} />
          <MarketingCycle projectKey={key} />
          <section className="post-section overview-card loop-section">
            <div className="section-h-row">
              <h2>Loop status</h2>
              <span className="muted small">Markaii Loop, this project</span>
            </div>
            {loopQ.isLoading && <div className="loading">Loading loop status…</div>}
            {loopQ.isError && <div className="error">Couldn’t load loop status: {loopQ.error.message}</div>}
            {loopQ.data && <LoopStatus stages={loopQ.data.stages} />}
          </section>
          <PillarEngagementSection projectKey={key} />
        </>
      )}

      {tab === 'posts' && <ProjectRecentPosts projectKey={key} />}

      {tab === 'stats' && <ProjectStats projectKey={key} />}

      {tab === 'plan' && (
        <>
          {draftBadge && (
            <section className="post-section">
              <div className="section-h-row">
                <h2>
                  Plan draft available
                  <span className="plan-draft-badge" style={{ marginLeft: '0.6rem' }}>
                    v{draftPlanQ.data.plan.version} draft
                  </span>
                </h2>
              </div>
              <PlanDiffCard
                active={{ plan: planQ.data.plan, overrides: planQ.data.overrides }}
                draft={{ plan: draftPlanQ.data.plan, overrides: draftPlanQ.data.overrides }}
                projectKey={key}
              />
            </section>
          )}
          <section className="post-section">
            <div className="section-h-row">
              <h2>Active marketing plan</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <IteratePlanButton projectKey={key} disabled={!planQ.data} />
                <RegeneratePlanButton projectKey={key} />
              </div>
            </div>
            {planQ.isLoading && <div className="loading">Loading plan…</div>}
            {planQ.error?.status === 404 && (
              <div className="plan-empty">
                <p>No plan yet for this project.</p>
                <p className="muted small">Use “Regenerate plan” above to generate the first version.</p>
              </div>
            )}
            {planQ.data && (
              <PlanCard plan={planQ.data.plan} overrides={planQ.data.overrides} versions={planQ.data.versions} />
            )}
          </section>
        </>
      )}

      {tab === 'pillars' && (
        <section className="post-section">
          <h2>Pillars</h2>
          <div className="pillar-table">
            <div className="pillar-row pillar-row-head">
              <span>Pillar</span>
              <span>Schedule (UTC)</span>
              <span>Approval</span>
              <span>Enabled</span>
            </div>
            {pillars.map((p) => (
              <PillarRow key={p.pillar_key} pillar={p} projectKey={key} />
            ))}
          </div>
        </section>
      )}

      {tab === 'connections' && <ConnectionsSection projectKey={key} project={project} />}

      {tab === 'samples' && <ProjectSamples projectKey={key} />}
    </div>
  );
}

/**
 * ProjectRecentPosts — project-scoped post list with status + date filters
 * and inline Approve/Reject. Owns its own filter state and query.
 */
function ProjectRecentPosts({ projectKey }) {
  const [filters, setFilters] = useState({ state: '', pillar: '', from: '', to: '' });
  const qs = buildPostsQuery({ ...filters, project: projectKey, limit: 50 });
  const postsQ = useQuery({
    queryKey: ['posts', { project: projectKey, ...filters, limit: 50 }],
    queryFn: ({ signal }) => apiGet(`/posts?${qs}`, { signal }),
  });
  return (
    <section className="post-section">
      <h2>Recent posts</h2>
      <PostFilters {...filters} onChange={setFilters} />
      {postsQ.isLoading && <div className="loading">Loading…</div>}
      {postsQ.data && postsQ.data.posts.length === 0 && <div className="empty">No posts match this filter.</div>}
      {postsQ.data && postsQ.data.posts.length > 0 && (
        <div className="post-table">
          <div className="post-row post-row-head post-row-rich">
            <span>Title</span>
            <span>State</span>
            <span>When</span>
            <span>Actions</span>
          </div>
          {postsQ.data.posts.map((p) => <PostListRow key={p.id} post={p} />)}
        </div>
      )}
    </section>
  );
}

/**
 * PillarEngagementSection — ADR-0019. Per-pillar impressions trend over
 * the last 30 days, plus rollup totals. Hidden until at least one
 * publication has produced metrics for this project. Pairs with the
 * Iterate Plan button below to give the owner a visual signal of which
 * pillars to push or cut.
 */
function PillarEngagementSection({ projectKey }) {
  const trendQ = useQuery({
    queryKey: ['engagement-pillars-timeseries', { project: projectKey, days: 30 }],
    queryFn: ({ signal }) =>
      apiGet(`/engagement/pillars/timeseries?project=${encodeURIComponent(projectKey)}&days=30&metric=impressions`, { signal }),
  });

  const grouped = useMemo(() => {
    const points = trendQ.data?.points || [];
    const byPillar = {};
    for (const pt of points) {
      if (!byPillar[pt.pillar]) byPillar[pt.pillar] = { values: [], total: 0 };
      byPillar[pt.pillar].values.push(pt.value);
      byPillar[pt.pillar].total += pt.value;
    }
    return byPillar;
  }, [trendQ.data?.points]);

  if (trendQ.isLoading || trendQ.isError) return null;
  const pillarsWithData = Object.keys(grouped).sort((a, b) => grouped[b].total - grouped[a].total);
  if (pillarsWithData.length === 0) return null;

  const fmt = (n) => Number(n).toLocaleString();
  return (
    <section className="post-section overview-card">
      <div className="section-h-row">
        <h2>Pillar performance — last 30 days</h2>
        <span className="muted small">Impressions per pillar (ranked)</span>
      </div>
      <div className="engagement-by-platform">
        {pillarsWithData.map((pk) => (
          <div key={pk} className="engagement-platform-row">
            <span className="pillar-tag">{pillarLabel(pk)}</span>
            <span><b>{fmt(grouped[pk].total)}</b> impressions</span>
            <span className="muted small">{grouped[pk].values.length} sample{grouped[pk].values.length === 1 ? '' : 's'}</span>
            <span
              className="engagement-platform-trend"
              title={`Impressions trend for ${pillarLabel(pk)}`}
            >
              <Sparkline
                values={grouped[pk].values}
                width={120}
                height={22}
                ariaLabel={`${pk} impressions trend`}
              />
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PillarRow({ pillar, projectKey }) {
  const queryClient = useQueryClient();
  const [optimistic, setOptimistic] = useState(pillar.enabled);
  const [fireMsg, setFireMsg] = useState(null);
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState(pillar.owner_direction || '');
  const [metric, setMetric] = useState(pillar.target_metric_override || '');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmAuto, setConfirmAuto] = useState(false);

  // Approval policy: manual review (default) vs auto-publish. Turning auto ON
  // is consequential (posts go live with no human stop), so it's confirm-gated;
  // turning it OFF is instant. Flagged posts always wait for review either way
  // (enforced server-side in runPillar).
  const isAuto = pillar.approval_policy === 'auto_publish';
  const approvalM = useMutation({
    mutationFn: (policy) =>
      apiPatch(`/projects/${projectKey}/pillars/${pillar.pillar_key}`, { approval_policy: policy }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      setConfirmAuto(false);
    },
    onError: (err) => {
      setConfirmAuto(false);
      setFireMsg({ kind: 'err', text: err.body?.detail || err.message });
      setTimeout(() => setFireMsg(null), 8000);
    },
  });

  const toggleM = useMutation({
    mutationFn: (nextEnabled) =>
      apiPatch(`/projects/${projectKey}/pillars/${pillar.pillar_key}`, { enabled: nextEnabled }),
    onMutate: (nextEnabled) => { setOptimistic(nextEnabled); },
    onError: () => { setOptimistic(pillar.enabled); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
    },
  });

  const directionM = useMutation({
    mutationFn: () => apiPatch(`/projects/${projectKey}/pillars/${pillar.pillar_key}`, {
      owner_direction: direction.trim() || null,
      target_metric_override: metric || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      setOpen(false);
      setConfirmDiscard(false);
    },
  });

  // Direction panel is dirty when the typed direction (trimmed, matching how
  // it's saved) or the pinned metric differ from the pillar's saved values.
  const directionDirty = () =>
    direction.trim() !== (pillar.owner_direction || '') || metric !== (pillar.target_metric_override || '');
  const resetDirection = () => {
    setDirection(pillar.owner_direction || '');
    setMetric(pillar.target_metric_override || '');
    setConfirmDiscard(false);
    setOpen(false);
  };
  const requestDirectionCancel = () => { if (directionDirty()) setConfirmDiscard(true); else resetDirection(); };

  const fireM = useMutation({
    mutationFn: () => apiPost(`/projects/${projectKey}/pillars/${pillar.pillar_key}/fire-now`),
    onSuccess: (res) => {
      if (res.skipped === 'idempotency_collision') {
        setFireMsg({ kind: 'warn', text: `Already ran today — see post ${res.post_id?.slice(0, 8)}…` });
      } else {
        setFireMsg({ kind: 'ok', text: `Fired: ${res.title || '(no title)'} (${res.attachmentCount || 0} asset${res.attachmentCount === 1 ? '' : 's'})` });
        queryClient.invalidateQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
        queryClient.invalidateQueries({ queryKey: ['loop-status', projectKey] });
      }
      setTimeout(() => setFireMsg(null), 8000);
    },
    onError: (err) => {
      setFireMsg({ kind: 'err', text: err.body?.detail || err.message });
      setTimeout(() => setFireMsg(null), 8000);
    },
  });

  const enabled = toggleM.isPending ? optimistic : pillar.enabled;

  return (
    <>
      <div className="pillar-row">
        <span className="pillar-tag">{pillar.pillar_key.replace(/_/g, ' ')}</span>
        <span className="pillar-schedule" title={pillar.cron_expr}>{humanCron(pillar.cron_expr)}</span>
        <button
          type="button"
          className={`pillar-approval${isAuto ? ' is-auto' : ''}`}
          disabled={approvalM.isPending}
          title={isAuto
            ? 'Auto — posts that pass the automated content check publish immediately. Click to switch back to manual review.'
            : 'Manual — every post waits in your Inbox for approval. Click to auto-publish clean posts.'}
          onClick={() => { if (isAuto) approvalM.mutate('require_approval'); else setConfirmAuto(true); }}
        >
          {approvalM.isPending ? 'Saving…' : (isAuto ? '⚡ Auto-publish' : 'Manual review')}
        </button>
        <button
          type="button"
          className={`pillar-toggle ${enabled ? 'on' : 'off'}`}
          disabled={toggleM.isPending}
          onClick={() => toggleM.mutate(!enabled)}
          title={`Click to ${enabled ? 'disable' : 'enable'} this pillar`}
        >
          <span className="pillar-toggle-knob" />
          <span className="pillar-toggle-label">{enabled ? 'on' : 'off'}</span>
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-icon pillar-fire-btn"
          disabled={fireM.isPending || !enabled}
          onClick={() => fireM.mutate()}
          title="Fire this pillar now (creates a fresh draft today)"
          aria-label="Fire this pillar now"
        >
          {fireM.isPending ? '…' : '⚡'}
        </button>
        <button
          type="button"
          className={`btn-compact pillar-direction-toggle${pillar.owner_direction ? ' has-direction' : ''}`}
          onClick={() => setOpen((o) => !o)}
          title="Set your direction for this pillar"
          aria-expanded={open}
        >
          {open ? 'Close' : (pillar.owner_direction ? <>Direction <span aria-hidden="true">✎</span></> : 'Direction')}
        </button>
      </div>
      {fireMsg && (
        <div className={`pillar-fire-msg ${fireMsg.kind}`}>{fireMsg.text}</div>
      )}
      {confirmAuto && (
        <div className="confirm-banner">
          <p>
            Auto-publish <b>{pillar.pillar_key.replace(/_/g, ' ')}</b>? New posts that pass the automated
            content check will publish immediately to every connected account — no Inbox stop. Posts the
            check flags still wait for your review.
          </p>
          <div className="confirm-banner-actions">
            <button type="button" className="btn-compact" onClick={() => setConfirmAuto(false)} disabled={approvalM.isPending}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => approvalM.mutate('auto_publish')} disabled={approvalM.isPending}>
              {approvalM.isPending ? 'Saving…' : 'Turn on auto-publish'}
            </button>
          </div>
        </div>
      )}
      {open && (
        <div className="pillar-direction-panel">
          <label className="login-field">
            <span>Your direction for <b>{pillar.pillar_key.replace(/_/g, ' ')}</b> <small className="muted">— how it should sound / what to cover. Overrides the AI's voice for this pillar.</small></span>
            <textarea
              rows={3}
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder="e.g. Keep this pillar technical and screenshot-led. Always show a real dashboard view, never stock photos."
            />
          </label>
          <label className="login-field pillar-metric-field">
            <span>Pin a primary metric <small className="muted">— overrides the plan's pick</small></span>
            <select value={metric} onChange={(e) => setMetric(e.target.value)}>
              {TARGET_METRIC_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {directionM.isError && (
            <div className="post-action-msg err">{directionM.error.body?.detail || directionM.error.message}</div>
          )}
          {confirmDiscard && (
            <div className="confirm-banner confirm-banner--danger">
              <p>Discard your changes to this pillar's direction?</p>
              <div className="confirm-banner-actions">
                <button type="button" className="btn-compact" onClick={() => setConfirmDiscard(false)}>Keep editing</button>
                <button type="button" className="btn btn-danger" onClick={resetDirection}>Discard changes</button>
              </div>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-compact" onClick={requestDirectionCancel} disabled={directionM.isPending}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => directionM.mutate()} disabled={directionM.isPending}>
              {directionM.isPending ? 'Saving…' : 'Save direction'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function IteratePlanButton({ projectKey, disabled }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);
  const [guidance, setGuidance] = useState('');
  const m = useMutation({
    mutationFn: () => apiPost(`/projects/${projectKey}/iterate-plan`, guidance.trim() ? { guidance: guidance.trim() } : {}),
    onSuccess: (res) => {
      setMsg({ kind: 'ok', text: `Draft v${res.version} ready — review below.` });
      queryClient.invalidateQueries({ queryKey: ['plan-draft', { project: projectKey }] });
      setOpen(false);
      setGuidance('');
      setTimeout(() => setMsg(null), 10000);
    },
    onError: (err) => {
      setMsg({ kind: 'err', text: err.body?.detail || err.message });
      setTimeout(() => setMsg(null), 10000);
    },
  });
  return (
    <>
      <button
        type="button"
        className="btn-compact"
        disabled={m.isPending || disabled}
        onClick={() => setOpen((o) => !o)}
        title={disabled ? 'No active plan yet — regenerate first' : 'Generate a new plan draft based on recent engagement + your guidance'}
      >
        {m.isPending ? 'Iterating…' : 'Iterate plan'}
      </button>
      {open && !m.isPending && (
        <div className="iterate-guidance-pop">
          <label className="login-field">
            <span>What should change? <small className="muted">— optional. The AI weighs recent engagement, then applies your steer on top.</small></span>
            <textarea
              rows={3}
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder="e.g. Push reels harder now that we can produce video. Drop the lifestyle angle. Aim for 1,000 followers, not 320."
              autoFocus
            />
          </label>
          <div className="form-actions">
            <button type="button" className="btn-compact" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => m.mutate()}>
              Create draft{guidance.trim() ? ' with guidance' : ''}
            </button>
          </div>
        </div>
      )}
      {msg && <div className={`pillar-fire-msg ${msg.kind}`}>{msg.text}</div>}
    </>
  );
}

function RegeneratePlanButton({ projectKey }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const m = useMutation({
    mutationFn: () => apiPost(`/projects/${projectKey}/regenerate-plan`),
    onSuccess: (res) => {
      setConfirming(false);
      setMsg({ kind: 'ok', text: `New plan v${res.version} active. Previous version archived.` });
      queryClient.invalidateQueries({ queryKey: ['plan', { project: projectKey }] });
      queryClient.invalidateQueries({ queryKey: ['project', projectKey] });
      queryClient.invalidateQueries({ queryKey: ['loop-status', projectKey] });
      setTimeout(() => setMsg(null), 10000);
    },
    onError: (err) => {
      setConfirming(false);
      setMsg({ kind: 'err', text: err.body?.detail || err.message });
      setTimeout(() => setMsg(null), 10000);
    },
  });
  return (
    <>
      <button
        type="button"
        className="btn-compact"
        disabled={m.isPending}
        onClick={() => setConfirming(true)}
      >
        {m.isPending ? 'Regenerating…' : 'Regenerate plan'}
      </button>
      {confirming && (
        <div className="confirm-banner">
          <p>Regenerate plan? This creates a new version and supersedes the active one.</p>
          <div className="confirm-banner-actions">
            <button type="button" className="btn-compact" onClick={() => setConfirming(false)} disabled={m.isPending}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => m.mutate()} disabled={m.isPending}>
              {m.isPending ? 'Regenerating…' : 'Yes, regenerate'}
            </button>
          </div>
        </div>
      )}
      {msg && <div className={`pillar-fire-msg ${msg.kind}`}>{msg.text}</div>}
    </>
  );
}

/**
 * LoopStatus — per-project, 8-stage Markaii Loop health table.
 *
 * Each row maps to a stage from ADR-0014 and shows a status icon + detail
 * string computed by GET /projects/:key/loop-status. Same status enum as
 * the Dashboard "Markaii Loop" overview card, but project-scoped and
 * data-driven (not hardcoded).
 */
// Glyphs use currentColor (set per-status by .loop-icon-* in the CSS) instead
// of emoji — emoji render inconsistently across OS/browsers (notably Windows)
// and can't be recolored or scaled with CSS.
const STATUS_GLYPH = { ok: '✓', warn: '!', fail: '✕', queued: '○' };
const STATUS_LABEL = { ok: 'Healthy', warn: 'Attention', fail: 'Action needed', queued: 'Not shipped' };

function LoopStatus({ stages }) {
  return (
    <div className="loop-status">
      {stages.map((s) => (
        <div key={s.num} className={`loop-row loop-row-${s.status}`}>
          <span className="loop-num">{s.num}</span>
          <span className="loop-name">{s.name}</span>
          <span
            className={`loop-icon loop-icon-${s.status}`}
            role="img"
            title={STATUS_LABEL[s.status] || s.status}
            aria-label={`${s.name}: ${STATUS_LABEL[s.status] || s.status}`}
          >
            <span aria-hidden="true">{STATUS_GLYPH[s.status] || '·'}</span>
          </span>
          <span className="loop-detail">{s.detail}</span>
        </div>
      ))}
      <div className="loop-legend">
        <span className="loop-legend-item"><span className="loop-dot loop-dot-ok" /> Healthy</span>
        <span className="loop-legend-item"><span className="loop-dot loop-dot-warn" /> Attention</span>
        <span className="loop-legend-item"><span className="loop-dot loop-dot-fail" /> Action needed</span>
        <span className="loop-legend-item"><span className="loop-dot loop-dot-queued" /> Not yet shipped</span>
      </div>
    </div>
  );
}

/**
 * EditableKpiTargets — the owner's living KPI targets (ADR-0027).
 * Shows kpi_targets (the auto-adjusting goals) falling back to kpis. The
 * owner can override any target; the next iteration nudges from these values.
 */
function EditableKpiTargets({ plan }) {
  const queryClient = useQueryClient();
  const base = plan.kpi_targets || plan.kpis || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);

  const startEdit = () => {
    setForm({
      followers_target_30d: base.followers_target_30d ?? '',
      engagement_rate_pct: base.engagement_rate_target != null ? (base.engagement_rate_target * 100) : '',
      saves_per_post_target: base.saves_per_post_target ?? '',
      posts_per_week_target: base.posts_per_week_target ?? '',
    });
    setEditing(true);
  };

  const saveM = useMutation({
    mutationFn: () => {
      const kpi_targets = {};
      if (form.followers_target_30d !== '') kpi_targets.followers_target_30d = Number(form.followers_target_30d);
      if (form.engagement_rate_pct !== '') kpi_targets.engagement_rate_target = Number(form.engagement_rate_pct) / 100;
      if (form.saves_per_post_target !== '') kpi_targets.saves_per_post_target = Number(form.saves_per_post_target);
      if (form.posts_per_week_target !== '') kpi_targets.posts_per_week_target = Number(form.posts_per_week_target);
      return apiPatch(`/plans/${plan.id}/targets`, { kpi_targets });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', { project: plan.project_key }] });
      setEditing(false);
    },
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (editing && form) {
    return (
      <>
        <h3 className="plan-section-h">30-day targets — your goals</h3>
        <div className="kpi-edit-grid">
          <label className="kpi-edit"><span>New followers</span><input type="number" min="0" value={form.followers_target_30d} onChange={set('followers_target_30d')} /></label>
          <label className="kpi-edit"><span>Engagement rate %</span><input type="number" min="0" step="0.1" value={form.engagement_rate_pct} onChange={set('engagement_rate_pct')} /></label>
          <label className="kpi-edit"><span>Saves / post</span><input type="number" min="0" value={form.saves_per_post_target} onChange={set('saves_per_post_target')} /></label>
          <label className="kpi-edit"><span>Posts / week</span><input type="number" min="0" value={form.posts_per_week_target} onChange={set('posts_per_week_target')} /></label>
        </div>
        {saveM.isError && <div className="post-action-msg err">{saveM.error.body?.detail || saveM.error.message}</div>}
        <div className="form-actions">
          <button type="button" className="btn-compact" onClick={() => setEditing(false)} disabled={saveM.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => saveM.mutate()} disabled={saveM.isPending}>
            {saveM.isPending ? 'Saving…' : 'Save targets'}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="section-h-row">
        <h3 className="plan-section-h">30-day targets — your goals</h3>
        <button type="button" className="btn-compact" onClick={startEdit}>Edit targets</button>
      </div>
      <div className="kpi-grid">
        <div className="kpi"><span className="kpi-val">{base.followers_target_30d ?? '—'}</span><span className="kpi-lbl">new followers</span></div>
        <div className="kpi"><span className="kpi-val">{base.engagement_rate_target != null ? `${(base.engagement_rate_target * 100).toFixed(1)}%` : '—'}</span><span className="kpi-lbl">engagement rate</span></div>
        <div className="kpi"><span className="kpi-val">{base.saves_per_post_target ?? '—'}</span><span className="kpi-lbl">saves / post</span></div>
        <div className="kpi"><span className="kpi-val">{base.posts_per_week_target ?? '—'}</span><span className="kpi-lbl">posts / week</span></div>
      </div>
    </>
  );
}

function PlanCard({ plan, overrides, versions }) {
  return (
    <div className="plan-card">
      <div className="plan-card-head">
        <span className="plan-version">v{plan.version}</span>
        <span className={`state state-${plan.status}`}>{plan.status}</span>
        <span className="plan-when">approved {fmtAgo(plan.approved_at || plan.created_at)}</span>
        {versions.length > 1 && (
          <span className="plan-history">· {versions.length} versions in history</span>
        )}
      </div>

      <h3 className="plan-section-h">Notes</h3>
      <p className="plan-notes">{plan.notes}</p>

      <EditableKpiTargets plan={plan} />

      <h3 className="plan-section-h">Pillar plays</h3>
      <div className="override-table">
        <div className="override-row override-row-head">
          <span>Pillar</span>
          <span>Cadence</span>
          <span>Target metric</span>
          <span>Value</span>
          <span>Voice adjustment</span>
        </div>
        {overrides.map((o) => (
          <div key={o.pillar_key} className="override-row">
            <span className="pillar-tag">{o.pillar_key.replace(/_/g, ' ')}</span>
            <span>{CADENCE_LABEL[o.cadence] || o.cadence}</span>
            <span>{METRIC_LABEL[o.target_metric] || o.target_metric}</span>
            <span className="override-target">{o.target_value}</span>
            <span className="override-voice">{o.voice_adjustment || <span className="muted">—</span>}</span>
          </div>
        ))}
      </div>

      <details className="plan-snapshots">
        <summary>Plan snapshots (positioning + audience + voice at the time of approval)</summary>
        <div className="kv">
          <div className="kv-row"><span className="kv-label">Positioning</span><pre>{plan.positioning_snapshot}</pre></div>
          <div className="kv-row"><span className="kv-label">Audience</span><pre>{plan.audience_snapshot}</pre></div>
          <div className="kv-row"><span className="kv-label">Voice</span><pre>{plan.voice_snapshot}</pre></div>
        </div>
      </details>
    </div>
  );
}

/**
 * PlanDiffCard — ADR-0010 side-by-side diff between the active and draft
 * plans. Owner promotes the draft to activate it (atomically supersedes
 * the active version) or discards it.
 */
function PlanDiffCard({ active, draft, projectKey }) {
  const queryClient = useQueryClient();
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null); // null | 'promote' | 'discard'

  const promoteM = useMutation({
    mutationFn: () => apiPost(`/plans/${draft.plan.id}/promote`),
    onSuccess: () => {
      setConfirm(null);
      setMsg({ kind: 'ok', text: `Draft v${draft.plan.version} is now active.` });
      queryClient.invalidateQueries({ queryKey: ['plan', { project: projectKey }] });
      queryClient.invalidateQueries({ queryKey: ['plan-draft', { project: projectKey }] });
      queryClient.invalidateQueries({ queryKey: ['loop-status', projectKey] });
    },
    onError: (err) => { setConfirm(null); setMsg({ kind: 'err', text: err.body?.detail || err.message }); },
  });
  const discardM = useMutation({
    mutationFn: () => apiDelete(`/plans/${draft.plan.id}`),
    onSuccess: () => {
      setConfirm(null);
      setMsg({ kind: 'ok', text: 'Draft discarded.' });
      queryClient.invalidateQueries({ queryKey: ['plan-draft', { project: projectKey }] });
    },
    onError: (err) => { setConfirm(null); setMsg({ kind: 'err', text: err.body?.detail || err.message }); },
  });

  const ak = active.plan.kpis || {};
  const dk = draft.plan.kpis || {};
  const activeOverridesByPillar = Object.fromEntries(active.overrides.map((o) => [o.pillar_key, o]));
  const draftOverridesByPillar = Object.fromEntries(draft.overrides.map((o) => [o.pillar_key, o]));
  const allPillars = Array.from(new Set([
    ...Object.keys(activeOverridesByPillar),
    ...Object.keys(draftOverridesByPillar),
  ])).sort();

  return (
    <div className="plan-card">
      <div className="plan-card-head">
        <span>Comparing active <b>v{active.plan.version}</b> → draft <b>v{draft.plan.version}</b></span>
        <span className="plan-when">draft created {fmtAgo(draft.plan.created_at)}</span>
      </div>

      <h3 className="plan-section-h">Notes (draft)</h3>
      <p className="plan-notes">{draft.plan.notes}</p>

      <h3 className="plan-section-h">KPI changes</h3>
      <div className="plan-diff-grid">
        <div className="plan-diff-label">Followers target (30d)</div>
        <DiffCell value={ak.followers_target_30d} />
        <DiffCell value={dk.followers_target_30d} compare={ak.followers_target_30d} />

        <div className="plan-diff-label">Engagement rate target</div>
        <DiffCell value={ak.engagement_rate_target} format={(v) => v != null ? `${(v * 100).toFixed(1)}%` : '—'} />
        <DiffCell value={dk.engagement_rate_target} compare={ak.engagement_rate_target} format={(v) => v != null ? `${(v * 100).toFixed(1)}%` : '—'} />

        <div className="plan-diff-label">Saves per post target</div>
        <DiffCell value={ak.saves_per_post_target} />
        <DiffCell value={dk.saves_per_post_target} compare={ak.saves_per_post_target} />

        <div className="plan-diff-label">Posts per week target</div>
        <DiffCell value={ak.posts_per_week_target} />
        <DiffCell value={dk.posts_per_week_target} compare={ak.posts_per_week_target} />
      </div>

      <h3 className="plan-section-h">Pillar changes</h3>
      <div className="override-table">
        <div className="override-row override-row-head">
          <span>Pillar</span>
          <span>Active cadence</span>
          <span>Draft cadence</span>
          <span>Active target</span>
          <span>Draft target</span>
        </div>
        {allPillars.map((pillarKey) => {
          const a = activeOverridesByPillar[pillarKey];
          const d = draftOverridesByPillar[pillarKey];
          const cadenceChanged = (a?.cadence || null) !== (d?.cadence || null);
          const targetChanged = (a?.target_value || null) !== (d?.target_value || null);
          return (
            <div key={pillarKey} className="override-row">
              <span className="pillar-tag">{pillarKey.replace(/_/g, ' ')}</span>
              <span className={!a ? 'plan-diff-cell is-removed' : ''}>{a ? (CADENCE_LABEL[a.cadence] || a.cadence) : '—'}</span>
              <span className={cadenceChanged ? 'plan-diff-cell is-changed' : ''}>{d ? (CADENCE_LABEL[d.cadence] || d.cadence) : '—'}</span>
              <span>{a ? `${METRIC_LABEL[a.target_metric] || a.target_metric}: ${a.target_value}` : '—'}</span>
              <span className={targetChanged ? 'plan-diff-cell is-changed' : ''}>{d ? `${METRIC_LABEL[d.target_metric] || d.target_metric}: ${d.target_value}` : '—'}</span>
            </div>
          );
        })}
      </div>

      <div className="plan-iteration-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={promoteM.isPending || discardM.isPending}
          onClick={() => setConfirm('promote')}
        >
          {promoteM.isPending ? 'Promoting…' : `Activate v${draft.plan.version}`}
        </button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={promoteM.isPending || discardM.isPending}
          onClick={() => setConfirm('discard')}
        >
          {discardM.isPending ? 'Discarding…' : 'Discard draft'}
        </button>
      </div>
      {confirm === 'promote' && (
        <div className="confirm-banner">
          <p>Promote v{draft.plan.version} to active? The current v{active.plan.version} will be superseded.</p>
          <div className="confirm-banner-actions">
            <button type="button" className="btn-compact" onClick={() => setConfirm(null)} disabled={promoteM.isPending}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={() => promoteM.mutate()} disabled={promoteM.isPending}>
              {promoteM.isPending ? 'Promoting…' : `Yes, activate v${draft.plan.version}`}
            </button>
          </div>
        </div>
      )}
      {confirm === 'discard' && (
        <div className="confirm-banner confirm-banner--danger">
          <p>Discard this draft? It will be deleted permanently.</p>
          <div className="confirm-banner-actions">
            <button type="button" className="btn-compact" onClick={() => setConfirm(null)} disabled={discardM.isPending}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={() => discardM.mutate()} disabled={discardM.isPending}>
              {discardM.isPending ? 'Discarding…' : 'Discard permanently'}
            </button>
          </div>
        </div>
      )}
      {msg && <div className={`pillar-fire-msg ${msg.kind}`}>{msg.text}</div>}
    </div>
  );
}

function DiffCell({ value, compare, format }) {
  const fmt = format || ((v) => v != null ? String(v) : '—');
  const changed = compare !== undefined && value !== compare;
  return (
    <div className={`plan-diff-cell ${changed ? 'is-changed' : ''}`}>{fmt(value)}</div>
  );
}
